import React from 'react';

import { userApis } from '@apis/userApis';
import bgLogin from '@assets/img/arts/bg-login.png';
import d2d from '@assets/img/icons/d2d-new.svg';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { TextInput } from '@components/Forms/TextInput';
import { Layout } from '@components/Layout';
import { SpinnerModal } from '@components/SpinnerModal';
import { useMutation } from '@hooks/useMutation';
import { useSearch } from '@hooks/useSearch';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { formStrings, createValidate, newPasswordField } from '@utils/forms';
import { createStrings } from '@utils/local';
import { PageMeta } from '@utils/pages';
import { Form, Formik } from 'formik';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: 'パスワード再設定',
    newPassword: '新しいパスワード(半角英数字8～16文字で入力してください)',
    confirmPassword: '新しいパスワードを再入力',
    success: 'パスワードを更新しました',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.backgroundCenter,
    flex: 1,
    backgroundImage: `url(${bgLogin})`,
  },
  title: {
    ...common.flexInline,
    ...common.biggerText,
    alignItems: 'center',
    fontSize: 30,
    lineHeight: '30px',
    color: theme.palette.text.primary,
    justifyContent: 'center',
    marginBottom: 50,
  },
  inner: {
    maxWidth: 680,
    margin: '0 auto',
    padding: '0 1rem',
  },
  alertContainer: {
    ...common.flexColumn,
    justifyContent: 'center',
    height: 130,
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '90px 100px',
    backgroundColor: theme.palette.background.paper,
  },
  logo: {
    display: 'block',
    width: 130,
    height: 30,
    alignSelf: 'center',
  },
  helpContainer: {
    ...common.flexInline,
    justifyContent: 'space-between',
  },
  helpLink: {
    ...common.baseButton,
    ...common.middleText,
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}));

const query = t.type({
  uidb36: t.string,
  token: t.string,
});

const schema = t.type({
  new_password1: newPasswordField(strings.newPassword),
  new_password2: newPasswordField(strings.confirmPassword),
});

type Values = t.TypeOf<typeof schema>;

const initialValues: Values = {
  new_password1: '',
  new_password2: '',
};

export const ResetPassword: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();

  const { search } = useSearch(query);

  const { mutateAsync, isLoading, isError, isSuccess, error, reset } = useMutation(
    async (values: Values) => {
      const response = await userApis.post('/password/reset/confirm/', {
        body: {
          ...values,
          uid: search.uidb36,
          token: search.token,
        },
      });

      if (!response[200]) {
        throw new FormError('ResetPassword', response[400]!);
      }
    },
  );

  return (
    <Layout title={strings.title}>
      <SpinnerModal open={isLoading} />
      <div className={classes.container}>
        <div className={classes.inner}>
          <Formik
            validate={createValidate(schema)}
            initialValues={initialValues}
            onSubmit={async (values) => {
              await mutateAsync(values);
              history.push('/auth/Login?passwordReset=true');
            }}
          >
            <>
              <div className={classes.alertContainer}>
                <Alert open={isError} severity="error" onClose={reset}>
                  {error instanceof FormError && error.getDisplayMessage()}
                </Alert>

                <Alert open={isSuccess} severity="success" onClose={reset}>
                  {strings.success}
                </Alert>
              </div>
              <Form className={classes.formContainer}>
                <div className={classes.title}>
                  <img src={d2d} className={classes.logo} alt="D2D Logo" />
                  <div style={{ width: '1rem' }} />
                  {strings.title}
                </div>

                <TextInput
                  name="new_password1"
                  type="password"
                  autoComplete="new-password"
                  label={strings.newPassword}
                  itemVariant="column"
                  iconBefore={
                    <LockOutlinedIcon
                      style={{ paddingRight: 10, width: '1.6rem', height: '1.6rem' }}
                    />
                  }
                />

                <TextInput
                  name="new_password2"
                  type="password"
                  autoComplete="new-password"
                  label={strings.confirmPassword}
                  itemVariant="column"
                  iconBefore={
                    <LockOutlinedIcon
                      style={{ paddingRight: 10, width: '1.6rem', height: '1.6rem' }}
                    />
                  }
                />

                <div style={{ height: 0 }} />

                <Button text={formStrings.confirm} color="primary" type="submit" />
              </Form>
            </>
          </Formik>
        </div>
      </div>
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
};
